import PropTypes from "prop-types";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  AccountBalanceWalletOutlined,
  Close as CloseIcon,
  InfoOutlined,
  PaymentsOutlined,
} from "@mui/icons-material";
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { formatDate, formatDeduction, formatMoney } from "@/features/shared";

const SummaryRow = ({ label, value, info, onInfoClick, muted, strong, valueColor }) => (
  <Stack
    direction="row"
    sx={{
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.75
    }}>
    <Stack direction="row" spacing={0.5} sx={{
      alignItems: "center"
    }}>
      <Typography
        variant="body2"
        color={muted ? "text.tertiary" : "text.secondary"}
        sx={{
          fontWeight: strong ? "bold" : undefined
        }}
      >
        {label}
      </Typography>
      {info && (
        <IconButton
          size="small"
          onClick={onInfoClick}
          sx={{ color: "primary.main", p: 0.25 }}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      )}
    </Stack>
    <Typography
      variant="body2"
      color={valueColor}
      sx={{
        fontWeight: strong ? "bold" : undefined
      }}
    >
      {value}
    </Typography>
  </Stack>
);

SummaryRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  info: PropTypes.bool,
  onInfoClick: PropTypes.func,
  muted: PropTypes.bool,
  strong: PropTypes.bool,
  valueColor: PropTypes.string,
};

export const FinancialSummarySection = ({ salesDownPayment }) => {
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);

  const grandTotal = salesDownPayment.grandTotal || 0;
  const returnedAmount = salesDownPayment.returnedAmount || 0;
  const outstandingAmount = salesDownPayment.outstandingAmount || 0;

  const allocations = salesDownPayment.salesDownPaymentAllocations || [];
  const payments = salesDownPayment.salesPayments || [];

  // "Terpakai/Retur" — a refunded down payment is no longer usable either.
  const totalApplied =
    allocations.reduce(
      (sum, allocation) => sum + (Number(allocation.amount) || 0),
      0,
    ) + returnedAmount;
  const remainingDownPayment = grandTotal - totalApplied;
  const totalPayment = payments.reduce(
    (sum, payment) => sum + (Number(payment.paidAmount) || 0),
    0,
  );

  const hasPayments = payments.length > 0;
  const hasAllocations = allocations.length > 0;

  return (
    <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
      {/* Settlement (Pembayaran) */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 2
            }}>
            <PaymentsOutlined fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle1">Pembayaran</Typography>
          </Stack>
          <SummaryRow label="Total" value={formatMoney(grandTotal)} />
          <SummaryRow
            label="Pembayaran"
            value={formatDeduction(totalPayment)}
            info={hasPayments}
            onInfoClick={() => setPaymentDialogOpen(true)}
          />
          {returnedAmount > 0 && (
            <SummaryRow
              label="Retur"
              value={formatDeduction(returnedAmount)}
              muted
            />
          )}
          <Divider sx={{ my: 0.5 }} />
          <SummaryRow
            label="Terutang"
            value={formatMoney(outstandingAmount)}
            strong
            valueColor={outstandingAmount > 0 ? "error.main" : "success.main"}
          />
        </Paper>
      </Grid>
      {/* Utilization (Penggunaan Uang Muka) */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 2
            }}>
            <AccountBalanceWalletOutlined
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
            <Typography variant="subtitle1">Penggunaan Uang Muka</Typography>
          </Stack>
          <SummaryRow label="Total" value={formatMoney(grandTotal)} />
          <SummaryRow
            label="Uang Muka Terpakai/Retur"
            value={formatDeduction(totalApplied)}
            info={hasAllocations}
            onInfoClick={() => setAllocationDialogOpen(true)}
          />
          <Divider sx={{ my: 0.5 }} />
          <SummaryRow
            label="Sisa Uang Muka"
            value={formatMoney(remainingDownPayment)}
            strong
            valueColor={
              remainingDownPayment > 0 ? "warning.main" : "success.main"
            }
          />
        </Paper>
      </Grid>
      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">Detail Pembayaran</Typography>
            <Chip
              label={`${payments.length} Transaksi`}
              size="small"
              color="primary"
            />
          </Stack>
          <IconButton onClick={() => setPaymentDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" sx={{
              justifyContent: "space-between"
            }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Total
              </Typography>
              <Typography variant="body1">{formatMoney(grandTotal)}</Typography>
            </Stack>

            {returnedAmount > 0 && (
              <Stack direction="row" sx={{
                justifyContent: "space-between"
              }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Total Retur
                </Typography>
                <Typography variant="body1">
                  -{formatMoney(returnedAmount)}
                </Typography>
              </Stack>
            )}

            {payments.map((payment) => (
              <Stack
                key={payment.id}
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Stack>
                  <Typography
                    component={Link}
                    to={`/sales/sales-payments/${payment.referenceNumber}`}
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {payment.referenceNumber}
                  </Typography>
                  {payment.transactionDate && (
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {formatDate(payment.transactionDate)}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="body1">
                  -{formatMoney(payment.paidAmount)}
                </Typography>
              </Stack>
            ))}

            <Divider />
            <Stack direction="row" sx={{
              justifyContent: "space-between"
            }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Sisa Terutang
              </Typography>
              <Typography
                variant="body1"
                color={outstandingAmount > 0 ? "error.main" : "success.main"}
                sx={{
                  fontWeight: "bold"
                }}
              >
                {formatMoney(outstandingAmount)}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      {/* Down Payment Usage Dialog */}
      <Dialog
        open={allocationDialogOpen}
        onClose={() => setAllocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">Detail Penggunaan Uang Muka</Typography>
            <Chip
              label={`${allocations.length} Transaksi`}
              size="small"
              color="primary"
            />
          </Stack>
          <IconButton onClick={() => setAllocationDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" sx={{
              justifyContent: "space-between"
            }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Total
              </Typography>
              <Typography variant="body1">{formatMoney(grandTotal)}</Typography>
            </Stack>

            {allocations.map((allocation) => {
              const reference =
                allocation.targetReferenceNumber ??
                allocation.targetSalesInvoice?.referenceNumber;
              const date =
                allocation.transactionDate ??
                allocation.targetSalesInvoice?.transactionDate;

              return (
                <Stack
                  key={allocation.id ?? reference}
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                  <Stack>
                    {reference ? (
                      <Typography
                        component={Link}
                        to={`/sales/sales-invoices/${reference}`}
                        sx={{
                          color: "primary.main",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {reference}
                      </Typography>
                    ) : (
                      <Typography variant="body1">-</Typography>
                    )}
                    {date && (
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {formatDate(date)}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body1">
                    -{formatMoney(allocation.amount || 0)}
                  </Typography>
                </Stack>
              );
            })}

            <Divider />
            <Stack direction="row" sx={{
              justifyContent: "space-between"
            }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Sisa Uang Muka
              </Typography>
              <Typography
                variant="body1"
                color={
                  remainingDownPayment > 0 ? "warning.main" : "success.main"
                }
                sx={{
                  fontWeight: "bold"
                }}
              >
                {formatMoney(remainingDownPayment)}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

FinancialSummarySection.propTypes = {
  salesDownPayment: PropTypes.object.isRequired,
};
